import { canonicalJson, createInitialState, sha256Text, type AppState, type Settings, type SyncEntity } from '@autoday/core';
import type { AppStore } from '../store/appStore.ts';
import type { DeviceState, OutboxOp, StorageAdapter } from '../store/storage.ts';
import { randomId } from '../platform.ts';

/**
 * Överföring från gästprofilen till ett konto – ENDAST efter aktivt val ("Lägg till mina lokala
 * aktiviteter i kontot"). Gästens original lämnas orörda. Poster behåller sina ID:n och importidentitet
 * (källa|UID), så ett upprepat försök kan aldrig skapa kopior: servern svarar duplicate/converged,
 * och redan överförda poster hoppas över lokalt. Allt skrivs i kontoprofilen i EN transaktion.
 */
export interface TransferCounts {
  events: number;
  importedItems: number;
  templates: number;
  notes: number;
  total: number;
}

interface Entry {
  key: string;
  entity: SyncEntity;
  id: string;
  doc: unknown;
  importId?: string;
}

const fp = (doc: unknown) => sha256Text(canonicalJson(doc)).slice(0, 20);

function entries(s: AppState): Entry[] {
  const out: Entry[] = [];
  for (const src of Object.values(s.sources)) out.push({ key: `importSource:${src.id}`, entity: 'importSource', id: src.id, doc: src });
  for (const si of Object.values(s.sourceItems)) out.push({ key: `sourceItem:${si.key}`, entity: 'sourceItem', id: si.key, doc: si, importId: si.lastSeenImportId });
  for (const e of Object.values(s.events)) if (!e.deleted) out.push({ key: `event:${e.id}`, entity: 'event', id: e.id, doc: e });
  for (const o of Object.values(s.overlays)) if (o.notes || o.done || o.categoryId || o.icon) out.push({ key: `overlay:${o.key}`, entity: 'overlay', id: o.key, doc: o });
  for (const t of Object.values(s.templates)) out.push({ key: `template:${t.id}`, entity: 'template', id: t.id, doc: t });
  return out;
}

/** Vad i gästprofilen som ännu inte lagts till i just detta konto (eller ändrats sedan dess). */
export function pendingTransfer(s: AppState, d: DeviceState, targetProfileId: string): { entries: Entry[]; counts: TransferCounts } {
  const done = d.guestTransfers?.[targetProfileId]?.keys ?? {};
  const pending = entries(s).filter((e) => done[e.key] !== fp(e.doc));
  const counts: TransferCounts = {
    events: pending.filter((e) => e.entity === 'event').length,
    importedItems: pending.filter((e) => e.entity === 'sourceItem').length,
    templates: pending.filter((e) => e.entity === 'template').length,
    notes: pending.filter((e) => e.entity === 'overlay').length,
    total: 0,
  };
  counts.total = counts.events + counts.importedItems + counts.templates + counts.notes;
  return { entries: pending, counts };
}

function getDoc(s: AppState, e: Entry): unknown {
  switch (e.entity) {
    case 'event':
      return s.events[e.id];
    case 'overlay':
      return s.overlays[e.id];
    case 'template':
      return s.templates[e.id];
    case 'sourceItem':
      return s.sourceItems[e.id];
    case 'importSource':
      return s.sources[e.id];
    default:
      return undefined;
  }
}

function putDoc(s: AppState, e: Entry): AppState {
  switch (e.entity) {
    case 'event':
      return { ...s, events: { ...s.events, [e.id]: e.doc as AppState['events'][string] } };
    case 'overlay':
      return { ...s, overlays: { ...s.overlays, [e.id]: e.doc as AppState['overlays'][string] } };
    case 'template':
      return { ...s, templates: { ...s.templates, [e.id]: e.doc as AppState['templates'][string] } };
    case 'sourceItem':
      return { ...s, sourceItems: { ...s.sourceItems, [e.id]: e.doc as AppState['sourceItems'][string] } };
    case 'importSource':
      return { ...s, sources: { ...s.sources, [e.id]: e.doc as AppState['sources'][string] } };
    default:
      return s;
  }
}

export function initialAccountState(guestSettings: Settings): AppState {
  const s = createInitialState();
  return { ...s, settings: { ...guestSettings, quietHours: { ...guestSettings.quietHours } } };
}

export interface TransferResult {
  added: number;
  /** Fanns redan i kontot med annat innehåll – lämnades orörda (ingen tyst överskrivning). */
  skipped: number;
  alreadyThere: number;
}

export async function transferGuestToAccount(guest: AppStore, account: StorageAdapter, targetProfileId: string): Promise<TransferResult> {
  const { entries: pending } = pendingTransfer(guest.state, guest.device, targetProfileId);
  let prev = await account.load();
  if (!prev) {
    const init = initialAccountState(guest.state.settings);
    await account.persist(null, init);
    prev = init;
  }
  let next: AppState = prev;
  const ops: OutboxOp[] = [];
  const doneKeys: Record<string, string> = {};
  const now = new Date().toISOString();
  let added = 0;
  let skipped = 0;
  let alreadyThere = 0;
  for (const e of pending) {
    const existing = getDoc(next, e);
    if (existing !== undefined && existing !== null) {
      if (fp(existing) === fp(e.doc)) alreadyThere++;
      else skipped++;
      // Noteras i båda fallen så att samma post inte erbjuds igen vid nästa inloggning.
      doneKeys[e.key] = fp(e.doc);
      continue;
    }
    next = putDoc(next, e);
    ops.push({ opId: randomId('op_'), createdAt: now, entity: e.entity, id: e.id, baseRevision: null, doc: e.doc, ...(e.importId ? { importId: e.importId } : {}) });
    doneKeys[e.key] = fp(e.doc);
    added++;
  }
  // Kategorier och kalendrar som de tillagda posterna behöver (t.ex. en importkälla eller egen kategori).
  let metaChanged = false;
  const calendars = { ...next.calendars };
  const categories = { ...next.categories };
  for (const ev of Object.values(next.events)) {
    if (!categories[ev.categoryId] && guest.state.categories[ev.categoryId]) {
      categories[ev.categoryId] = guest.state.categories[ev.categoryId]!;
      metaChanged = true;
    }
    if (!calendars[ev.calendarId] && guest.state.calendars[ev.calendarId]) {
      calendars[ev.calendarId] = guest.state.calendars[ev.calendarId]!;
      metaChanged = true;
    }
  }
  for (const src of Object.values(next.sources)) {
    if (!calendars[src.calendarId] && guest.state.calendars[src.calendarId]) {
      calendars[src.calendarId] = guest.state.calendars[src.calendarId]!;
      metaChanged = true;
    }
  }
  if (metaChanged) {
    next = { ...next, calendars, categories };
    ops.push({ opId: randomId('op_'), createdAt: now, entity: 'meta', id: 'settings', baseRevision: null, doc: { settings: next.settings, calendars, categories } });
  }
  if (ops.length) await account.persist(prev, next, ops);
  // Först när kontoprofilen sparats: notera vad som lagts till (gästens original rörs inte).
  await guest.updateDevice((d) => ({
    ...d,
    guestTransfers: { ...d.guestTransfers, [targetProfileId]: { at: now, keys: { ...(d.guestTransfers?.[targetProfileId]?.keys ?? {}), ...doneKeys } } },
  }));
  return { added, skipped, alreadyThere };
}
